import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';

import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { DomainEvent } from '@app/models/domain-event';
import { CommonConstants } from '@core/common-constants';
import { ApiEndPoints, DataService } from '@core/services/data.service';
import { EntitylementTypes } from '@lemans/shared';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action } from '@ngrx/store';
import { AGGridEntitlement, setDomainMessageReceived } from '@ra-state';
import { CommonService, ConfigService } from '@rockwell-automation-inc/service';
import { ErrorHandlers } from '@servicesV2/apierror-handlers.service';
import { CommandRequestBuilderService } from '@servicesV2/command-request-builder.service';
import { CommandRequest, EntitlementEvent, TenantEvent, UserEvent } from '@servicesV2/command-request.service';
import { LoggerService } from '@servicesV2/logger.service';
import { PermissionsService, PermsEvaluationResult, PolicyData } from '@servicesV2/permissions.service';
import * as R from 'remeda';
import { Observable, combineLatest, forkJoin, of, throwError } from 'rxjs';
import { catchError, combineLatestWith, concatMap, filter, map, mergeMap, switchMap } from 'rxjs/operators';
import { mergeTenantDetailEntitlements, openDialogWrapper$, replaceTenantLogoUrlHost } from '../../v2/shared/utils';
import { acceptEULASuccess, initAppRoute, navigateTo } from '../control-page';
import { initializeTarget, updateHarnessOnNoTenantSelected } from '../feature-flags';
import { acceptInvitationSuccess } from '../invitation/store';
import {
  AccessibleTenant,
  CreateTenantResponse,
  EffectiveRole,
  ITenantDetail,
  PendingService,
  PendingServiceV2,
  ROLES_WITH_MANAGE_ACCESS,
  Resource,
  TenantUtilityTokens,
  TransferOwnershipType,
  UploadLogoPreOperation,
  anyObjectTo,
  toUserId,
} from '../lemans-app.model';
import { closeAllOpenDialogs } from '../modal/modal.action';
import { UiStateActions } from '../ui-state/store';
import { UserDataFacade } from './user-data-facade';
import {
  addAccess,
  addAccessSuccess,
  applyCustomProvisioningEntitlement,
  applyEntitlementCancelled,
  applyRegularEntitlement,
  archiveTenant,
  archiveTenantSuccess,
  changeParentOrganization,
  changeParentOrganizationSuccess,
  checkUserCanChangeTenantHierarchy,
  clearOrgEntitlementsRefresh,
  clearRefreshTenantListGrid,
  clearUserEntitlementsGridRefresh,
  clearUserRolesGridRefresh,
  convertToParentOrganization,
  convertToParentOrganizationSuccess,
  convertToSubOrganization,
  convertToSubOrganizationSuccess,
  createChildTenant,
  createSelectedFakeEntitlements,
  createTenant,
  createTenantSuccess,
  customProvisioningCompleted,
  deallocateEntitlement,
  emptyAction,
  generateInviteCode,
  generateInviteCodeSuccess,
  getAllTenantResourceRoles,
  getLoggedInUserRoleHierarchy,
  getSuggestedTenantByCode,
  getTenantDetails,
  getTenantEffectiveRoles,
  getTenantEffectiveRolesAndServices,
  getTenantResourceHierarchy,
  getTenantServices,
  getTenantUsers,
  getTenantUtilityTokens,
  getUnallocatedEntitlementsCount,
  getUserMembership,
  getUserPreferences,
  getUserPreferencesSuccess,
  getUserResources,
  getUserRolesForTenant,
  initializeCustomProvisioning,
  leaveCustomProvisioning,
  leaveTenant,
  navigateBasedOnParentRole,
  noLastAccessedTenant,
  refreshOrgEntitlements,
  refreshTenantListGrid,
  refreshUserEntitlementsGrid,
  refreshUserRolesGrid,
  removeAccess,
  setAllTenantResourceRolesSuccess,
  setCanChangeTenantHierarchy,
  setCurrentLanguage,
  setFavoriteApps,
  setFavoriteAppsSuccess,
  setLoggedInUserRoleHierarchy,
  setNoSuggestedTenantByCodeFound,
  setPreferences,
  setPreferencesSuccess,
  setSuggestedTenantByCodeSuccess,
  setTenantDetails,
  setTenantEffectiveRoles,
  setTenantId,
  setTenantLogo,
  setTenantResourceHierarchy,
  setTenantServices,
  setTenantUsers,
  setTenantUtilityTokens,
  setUnallocatedEntitlementsCount,
  setUserMembership,
  setUserPermissionsEvalResult,
  setUserResourcesSuccess,
  setUserRolesForTenantSuccess,
  shareFeedback,
  shareFeedbackSuccess,
  switchTenant,
  transferOwnership,
  unarchiveTenant,
  unarchiveTenantSuccess,
  updateAccess,
  updateAccessibleTenants,
  updatePreferredLanguage,
  updateTenant,
  updateTenantNameSuccess,
  updateTenantSuccess,
  updateUserDescriptionForAllocationId,
  updateUserDescriptionOnFailure,
  updateUserDescriptionOnSuccess,
  uploadOrgLogo,
  uploadOrgLogoSuccess,
  userLosesAccessOnParentTenant,
  waitForCustomProvisioningComplete,
} from './user-data.action';
import { TENANT_MGMT_ROUTES, UserMembership } from './user.model';

@Injectable({
  providedIn: 'root',
})
export class UserDataEffects {
  private readonly actions$ = inject(Actions);
  private readonly userDataFacade = inject(UserDataFacade);
  private readonly router = inject(Router);
  private readonly dataService = inject(DataService);
  private readonly commandRequestBuilderService = inject(CommandRequestBuilderService);
  private readonly navbarCommonService = inject(CommonService);
  private readonly errorHandlers = inject(ErrorHandlers);
  private readonly policyService = inject(PermissionsService);
  private readonly logger = inject(LoggerService).withContext('UserDataEffects');
  private readonly configService = inject(ConfigService);

  private readonly DASHBOARD_PATH = '/dashboard';
  private readonly ENTITLEMENT_PATH = '/entitlement';
  openDialogWrapper$ = openDialogWrapper$;
  userID$ = this.userDataFacade.userID$;
  currentCustomProvisioningData$ = this.userDataFacade.currentCustomProvisioningData$;
  loadUserPreferences$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(acceptEULASuccess, getUserPreferences),
      combineLatestWith(this.userID$),
      switchMap(([_, userID]) => {
        return this.dataService.getUser$(String(userID)).pipe(
          catchError((error: unknown) => {
            const httpError = error as HttpErrorResponse;
            this.userDataFacade.getUserPreferencesFailed(httpError);
            return throwError(() => error);
          }),
        );
      }),
      switchMap((userPreferences) => {
        const { eulaAcceptanceRequired, preferences, accessibleTenants } = userPreferences;
        const actions: Action[] = [];
        const { lastAccessedTenantId } = preferences || {};
        actions.push(
          initAppRoute({
            payload: {
              eulaAcceptanceRequired: eulaAcceptanceRequired,
              lastAccessedTenantId: lastAccessedTenantId,
              accessibleTenants: accessibleTenants || [],
            },
          }),
          getUserPreferencesSuccess({ value: userPreferences }),
        );
        return actions;
      }),
    );
  });

  getTenantEffectiveRolesAndServices$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(getTenantEffectiveRolesAndServices),
      concatLatestFrom(() => [this.userID$]),
      switchMap(([payload, userId]) => {
        return forkJoin({
          effectiveRoles$: this.dataService.getTenantEffectiveRoles$(payload.tenantId, userId || ''),
          services$: this.dataService.getTenantServices$(String(userId), payload.tenantId),
          payload$: of(payload),
        });
      }),
      // eslint-disable-next-line sonarjs/function-return-type
      switchMap((response) => {
        const effectiveRoles = response.effectiveRoles$;
        const services = response.services$;
        const tenantRole = effectiveRoles.find((role) => role.resourceType === 'Tenant');
        if (!tenantRole) {
          this.logger.error(`Tenant role not found for tenantId ${response.payload$.tenantId}`);

          return throwError(() => new Error('tenant role not found...'));
        }
        const actions: Action[] = [
          setTenantEffectiveRoles({ effectiveRoles: effectiveRoles, navigateTo: response.payload$.navigateTo }),
          setTenantServices({ payload: services }),
          ...(ROLES_WITH_MANAGE_ACCESS.includes(tenantRole.role)
            ? [getTenantUtilityTokens({ value: response.payload$.tenantId })]
            : []),
          getTenantDetails({ value: response.payload$.tenantId }),
        ];
        return actions;
      }),
    );
  });

  evaulatePermissions$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(setTenantEffectiveRoles),
      switchMap((payload) => {
        const policyInput: PolicyData<EffectiveRole[]> = {
          input: payload.effectiveRoles,
        };
        return this.policyService.evaluate$<EffectiveRole[], PermsEvaluationResult>(policyInput);
      }),
      map((result) => {
        return setUserPermissionsEvalResult({ result: result });
      }),
      catchError((error: unknown) => {
        // Ensure permissions evaluation failures do not terminate the effect stream
        // and fall back to a safe default permissions result.
        this.logger.error('Error while evaluating user permissions based on OPA policy', error);
        return throwError(() => error);
      }),
    );
  });

  getTenantResourceRoles$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(getAllTenantResourceRoles),
      switchMap((payload) => {
        return this.dataService.getAllTenantResourceRoles$(payload.payload);
      }),
      switchMap((resourceRoles) => {
        return [setAllTenantResourceRolesSuccess({ payload: resourceRoles })];
      }),
    );
  });

  getTenantUsers$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(getTenantUsers),
      switchMap((payload) => {
        return this.dataService.getTenantHierarchyUsers$(payload.payload);
      }),
      map((response) => {
        return setTenantUsers({ selectedTenantUsers: response });
      }),
    );
  });

  getUserResourceRoles$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(getUserRolesForTenant),
      switchMap((payload) => {
        return this.dataService.getAllTenantResourceRoles$(payload.tenantId, payload.userId);
      }),
      switchMap((resourceRoles) => {
        return [setUserRolesForTenantSuccess({ payload: resourceRoles })];
      }),
    );
  });

  archiveTenant$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(archiveTenant),
      switchMap((archiveTenantPayload) => {
        const payload = archiveTenantPayload.archiveTenantPayload;
        const commandRequest = this.commandRequestBuilderService
          .new(`${ApiEndPoints.Tenant}archived?state=true`, 'PUT', (domainMessage) => {
            return domainMessage.type === TenantEvent.Archived;
          })
          .withTenantHeader(payload.tenantId)
          .withBody({})
          .withContext({ tenantName: payload.tenantName })
          .withErrorHandler({
            [HttpStatusCode.BadRequest]: this.errorHandlers.handleArchiveTenantErrors,
            [HttpStatusCode.Forbidden]: this.errorHandlers.handleArchiveTenantErrors,
          })
          .withWaitOn202Accepted();
        return combineLatest([commandRequest.invoke$(), of(payload)]);
      }),
      switchMap(([domainResponse, payload]) => {
        if (domainResponse.errorActions) {
          return domainResponse.errorActions;
        }
        const redirectionPath = payload.isSubOrg
          ? `${TENANT_MGMT_ROUTES.ORGANIZATIONS}/${payload.parentId}/suborgs`
          : TENANT_MGMT_ROUTES.ORGANIZATIONS;
        const actions: Action[] = [];
        actions.push(
          UiStateActions.displayMessage({
            payload: {
              message: `${payload.tenantName} has been archived and is no longer accessible to members.`,
              type: 'Success',
            },
          }),
        );
        if (payload.isSubOrg && payload.parentId) {
          actions.push(
            setPreferences({ tenantId: payload.parentId }),
            setTenantId({ value: payload.parentId, navigateTo: redirectionPath }),
          );
        } else {
          actions.push(
            navigateTo({ path: redirectionPath }),
            noLastAccessedTenant(),
            updateHarnessOnNoTenantSelected(),
          );
        }
        actions.push(archiveTenantSuccess());
        return actions;
      }),
    );
  });

  unarchiveTenant$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(unarchiveTenant),
      switchMap((payload) => {
        const commandRequest = this.commandRequestBuilderService
          .new(`${ApiEndPoints.Tenant}archived?state=false`, 'PUT', (domainMessage) => {
            return domainMessage.type === TenantEvent.Unarchived;
          })
          .withTenantHeader(payload.tenant.id)
          .withBody({})
          .withWaitOn202Accepted();
        return combineLatest([commandRequest.invoke$(), of(payload)]);
      }),
      switchMap(([domainResponse, payload]) => {
        if (domainResponse.errorActions) {
          return domainResponse.errorActions;
        }
        const actions: Action[] = [];
        actions.push(
          clearRefreshTenantListGrid(),
          UiStateActions.displayMessage({
            payload: {
              message: `Your organization ${payload.tenant.shortName} was successfully restored. It is now available for all current members to use.`,
              type: 'Success',
              customBtn: {
                label: 'Go to Applications',
                navigateTo: this.DASHBOARD_PATH,
                navigationOptions: {
                  actionType: 'tenantChange',
                  targetTenantId: payload.tenant.id,
                },
              },
            },
          }),
          unarchiveTenantSuccess(),
          refreshTenantListGrid(),
        );
        return actions;
      }),
    );
  });

  getTenantDetails$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(getTenantDetails, refreshOrgEntitlements),
      switchMap((tenantId) => {
        return this.navbarCommonService.getTenant$(tenantId.value).pipe(
          map((tenant) => ({
            ...tenant,
            logoUrl: replaceTenantLogoUrlHost(tenant.logoUrl, this.configService.config.csPortalUrl),
          })),
          map((tenant: ITenantDetail) => mergeTenantDetailEntitlements(tenant)),
        );
      }),
      switchMap((tenantDetail) => {
        return [setTenantDetails({ payload: tenantDetail })];
      }),
    );
  });

  updateHarnessTargetFlag$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(setTenantDetails, updateHarnessOnNoTenantSelected),
      concatLatestFrom(() => this.userDataFacade.userData$),
      switchMap(([payload, userData]) => {
        // When action is updateHarnessOnNoTenantSelected, tenantId/tenantName are undefined
        // so Harness re-initializes the target with no tenant context (top-level archive / leave)
        const tenantDetails = 'payload' in payload ? payload.payload : undefined;
        return [
          initializeTarget({
            payload: {
              identifier: toUserId(String(userData.userId)),
              attributes: {
                CurrentTenantId: tenantDetails?.id,
                CurrentTenantName: tenantDetails?.name,
                Type: 'User',
                Domain: CommonConstants.getEmailDomain(userData.email),
              },
            },
          }),
        ];
      }),
    );
  });

  setTenantId$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(setTenantId),
      switchMap((payload) => {
        const tenantId = payload.value;
        this.navbarCommonService.setCurrentTenantId(tenantId);
        return [getTenantEffectiveRolesAndServices({ tenantId: tenantId, navigateTo: payload.navigateTo })];
      }),
    );
  });

  setPreferences$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(setPreferences),
      concatLatestFrom(() => this.userID$),
      switchMap(([payload, userId]) => {
        return combineLatest([
          this.dataService.updateUserPreferences$(String(userId), {
            preferences: [{ name: 'lastAccessedTenantId', value: String(payload.tenantId) }],
          }),
          of(payload.tenantId),
        ]);
      }),
      switchMap(([_, tenantId]) => {
        return [setPreferencesSuccess({ tenantId })];
      }),
    );
  });

  udpatePreferredLanguage$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(updatePreferredLanguage),
      concatLatestFrom(() => this.userID$),
      switchMap(([payload, userId]) => {
        return combineLatest([
          this.dataService.updateUserPreferences$(String(userId), {
            preferences: [{ name: 'language', value: payload.preferredLanguage }],
          }),
          of(payload),
        ]);
      }),
      switchMap(([_response, payload]) => {
        return [setCurrentLanguage({ preferredLanguage: payload.preferredLanguage })];
      }),
    );
  });

  setFavoriteApps$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(setFavoriteApps),
      concatLatestFrom(() => this.userID$),
      switchMap(([payload, userId]) => {
        return combineLatest([
          this.dataService.updateUserPreferences$(String(userId), {
            preferences: [
              {
                name: 'favoriteApps',
                value: JSON.stringify(payload.favoriteApps),
              },
            ],
          }),
          of(payload.favoriteApps),
        ]);
      }),
      switchMap(([_response, favoriteApps]) => {
        return [setFavoriteAppsSuccess({ favoriteApps })];
      }),
    );
  });

  createTenant$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(createTenant),
      switchMap((payload) => {
        const commandRequest: CommandRequest = this.commandRequestBuilderService
          .new(
            ApiEndPoints.Tenants,
            'POST',
            // eslint-disable-next-line wrap-iife
            (function (countOfEventsToWaitFor: number, eventType: string): (domainMessage) => boolean {
              let countOfEventsReceived: number = 0;
              return (domainMessage): boolean => {
                if (domainMessage.type === eventType) {
                  countOfEventsReceived += 1;
                }
                return countOfEventsToWaitFor === countOfEventsReceived;
              };
            })(payload.payload.trialServices.length, TenantEvent.ServiceAddedV2),
          )
          .withIdempotencyKey(payload.idempotencyKey)
          .withBody(payload.payload);

        return combineLatest([commandRequest.invoke$(), of(payload.blobImage), of(payload.payload.name)]);
      }),
      switchMap(([response, blobImage, organizationName]) => {
        if (response.error && response.errorActions) {
          return response.errorActions;
        }

        const newTenantId = (response.response?.body as CreateTenantResponse)?.tenantId;
        const actions: Action[] = [];
        this.navbarCommonService.createNewTenant(newTenantId, organizationName);
        actions.push(
          switchTenant({ value: newTenantId }),
          createTenantSuccess({
            payload: {
              newTenantId: newTenantId,
              blobImage: blobImage,
            },
          }),
        );
        return actions;
      }),
    );
  });

  createChildTenant$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(createChildTenant),
      switchMap((payload) => {
        const commandRequest: CommandRequest = this.commandRequestBuilderService
          .new(
            `${ApiEndPoints.Tenant}hierarchy/${payload.idempotencyKey}/create`,
            'PUT',
            // eslint-disable-next-line wrap-iife
            (function (countOfEventsToWaitFor: number, eventType: string): (domainMessage) => boolean {
              let countOfEventsReceived: number = 0;
              return (domainMessage): boolean => {
                if (domainMessage.type === eventType) {
                  countOfEventsReceived += 1;
                }
                return countOfEventsToWaitFor === countOfEventsReceived;
              };
            })(payload.payload.trialServiceKinds.length, TenantEvent.ServiceAddedV2),
          )
          .withBody(payload.payload)
          .withWaitOn202Accepted()
          .withErrorHandler({
            [HttpStatusCode.BadRequest]: this.errorHandlers.handleChildTenantErrors,
            [HttpStatusCode.Conflict]: this.errorHandlers.handleChildTenantErrors,
          });

        return combineLatest([commandRequest.invoke$(), of(payload.blobImage), of(payload)]);
      }),
      switchMap(([response, blobImage, reqPayload]) => {
        const subOrganizationName = reqPayload.payload.name;
        if (response.error && response.errorActions) {
          return response.errorActions;
        }

        const newChildTenantId = (response.response?.body?.info as CreateTenantResponse)?.tenantId;
        const actions: Action[] = [];
        this.navbarCommonService.createNewTenant(newChildTenantId, subOrganizationName);
        actions.push(
          switchTenant({ value: newChildTenantId }),
          createTenantSuccess({
            payload: {
              newTenantId: newChildTenantId,
              blobImage: blobImage,
            },
          }),
        );
        return actions;
      }),
    );
  });

  uploadTenantLogoPostCreate$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(createTenantSuccess),
      switchMap((payload) => {
        const actions: Action[] = [];
        if (payload.payload.blobImage.size > 0) {
          actions.push(
            uploadOrgLogo({
              blobImage: payload.payload.blobImage,
              preOperation: UploadLogoPreOperation.CreateTenant,
              tenantId: payload.payload.newTenantId,
            }),
          );
        }
        return actions;
      }),
    );
  });

  uploadOrgLogo$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(uploadOrgLogo),
      switchMap((payload) => {
        const request$ = this.dataService.getUploadLogoUrl$(String(payload.tenantId));
        return combineLatest([request$, of(payload)]);
      }),
      switchMap(([logoUrl, payload]) => {
        const request$ = this.dataService.uploadOrgLogo$(logoUrl.url, payload.blobImage);
        return combineLatest([request$, of(payload)]);
      }),
      switchMap(([response, payload]) => {
        const etag = response.headers.get('ETag');
        const actions: Action[] = [];
        actions.push(setTenantLogo({ tenantId: payload.tenantId, etag: etag ?? undefined }));
        actions.push(uploadOrgLogoSuccess());
        this.mapActionsForUploadOrgLogo(actions, payload.preOperation, payload.tenantId);
        return actions;
      }),
    );
  });

  updateTenant$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(updateTenant),
      switchMap((payload) => {
        const commandRequest = this.commandRequestBuilderService
          .new(`${ApiEndPoints.Tenant}`, 'PUT', (domainMessage) => {
            return (
              domainMessage.type === TenantEvent.TenantUpdated ||
              domainMessage.type === TenantEvent.TenantNameUpdated ||
              domainMessage.type === TenantEvent.TenantDomainsUpdated
            );
          })
          .withTenantHeader(payload.payload.tenantId)
          .withBody(payload.payload)
          .withWaitOn200Ok();

        return combineLatest([commandRequest.invoke$(), of(payload.blobImage), of(payload.payload.name)]);
      }),
      switchMap(([response, blobImage, tenantName]) => {
        if (response.error && response.errorActions) {
          return response.errorActions;
        }

        const tenantId = response.message?.context.tenantId;
        const actions: Action[] = [];
        if (blobImage) {
          actions.push(
            uploadOrgLogo({
              blobImage: blobImage,
              preOperation:
                response.message?.type === TenantEvent.TenantNameUpdated
                  ? UploadLogoPreOperation.UpdateTenantName
                  : UploadLogoPreOperation.UpdateTenant,
              tenantId: String(tenantId),
            }),
          );
        } else {
          actions.push(updateTenantSuccess());
          if (
            response.message?.type === TenantEvent.TenantNameUpdated ||
            response.message?.type === TenantEvent.TenantUpdated ||
            response.message?.type === TenantEvent.TenantDomainsUpdated
          ) {
            actions.push(updateTenantNameSuccess({ value: tenantName }), getTenantDetails({ value: String(tenantId) }));
            this.navbarCommonService.setCurrentTenantIdWithNameUpdate(String(tenantId).replaceAll('-', ''), tenantName);
          }
          actions.push(
            UiStateActions.displayMessage({
              payload: { message: 'Organization updated successfully.', type: 'Success' },
            }),
          );
        }
        return actions;
      }),
    );
  });

  generateInviteCode$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(generateInviteCode),
      switchMap((payload) => {
        const commandRequest = this.commandRequestBuilderService
          .new(`${ApiEndPoints.Tenant}invitecode`, 'POST', TenantEvent.InviteCodeGenerated)
          .withTenantHeader(payload.tenantId)
          .withWaitOn200Ok();
        return combineLatest([commandRequest.invoke$(), of(payload)]);
      }),
      switchMap(([response, payload]) => {
        if (response.error && response.errorActions) {
          return response.errorActions;
        }

        return [
          generateInviteCodeSuccess(),
          getTenantDetails({ value: payload.tenantId }),
          UiStateActions.displayMessage({
            payload: {
              message: 'You have created a new invite link successfully!',
              type: 'Success',
            },
          }),
        ];
      }),
    );
  });

  switchTenant$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(switchTenant),
      switchMap((payload) => {
        return [
          setPreferences({ tenantId: payload.value }),
          setTenantId({ value: payload.value, navigateTo: payload.navigateTo }),
        ];
      }),
    );
  });

  shareFeedback$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(shareFeedback),
      switchMap((payload) => {
        return this.dataService.shareFeedback$(payload.payload);
      }),
      map(() => {
        return shareFeedbackSuccess();
      }),
    );
  });

  getSuggestedTenantByCode$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(getSuggestedTenantByCode),
      filter((value) => value.inviteCode != null),
      switchMap((value) => {
        return this.dataService.getSuggestedTenantByCode$(String(value.inviteCode));
      }),
      map((suggestedTenants) => {
        if (suggestedTenants.length) {
          return setSuggestedTenantByCodeSuccess({ payload: suggestedTenants[0] });
        }
        return setNoSuggestedTenantByCodeFound({ payload: true });
      }),
    );
  });

  getUserResources$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(getUserResources),
      filter((value) => value.value != null),
      switchMap((value) => {
        return this.dataService.getUserResources$(String(value.value));
      }),
      switchMap((resources: Resource[]) => {
        return [setUserResourcesSuccess({ payload: resources })];
      }),
    );
  });

  createSelectedFakeEntitlements$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(createSelectedFakeEntitlements),
      switchMap(({ codes, quantity, duration, effectiveDate }) => {
        let url = `/api/users/entitlements/update?quantity=${quantity}&validityDays=${duration}`;
        if (effectiveDate) {
          url = `${url}&effectiveDate=${effectiveDate}`;
        }
        const commandRequest = this.commandRequestBuilderService
          .new(
            `${url}`,
            'POST',
            (msg) => msg.type === TenantEvent.EntitlementAddedV2 || msg.type === TenantEvent.TokenEntitlementAllocated,
          )
          .withBody(codes.map((code) => code.catalogCode))
          .withWaitOn200Ok();

        return commandRequest.invoke$();
      }),
      switchMap((response) => {
        if (response.error && response.errorActions) {
          return response.errorActions;
        }
        return [refreshUserEntitlementsGrid(), getUnallocatedEntitlementsCount(), clearUserEntitlementsGridRefresh()];
      }),
    );
  });

  applyRegularEntitlement$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(applyRegularEntitlement),
      switchMap((value) => {
        const url = `/api/tenant/entitlements/${value.entitlementData.entitlement.id}`;
        const productFamilyName = (value.entitlementData?.entitlement as AGGridEntitlement)?.productFamily;
        const commandRequest = this.commandRequestBuilderService
          .new(
            url,
            'POST',
            (msg) => msg.type === TenantEvent.EntitlementAddedV2 || msg.type === TenantEvent.TokenEntitlementAllocated,
          )
          .withTenantHeader(value.tenantId)
          .withBody(value.payload)
          .withContext({ productFamilyName, requestInfo: value })
          .withCustomTimer('7000')
          .withWaitOn202Accepted()
          .withErrorHandler({
            [HttpStatusCode.BadRequest]: this.errorHandlers.handleEntitlementErrors,
            [HttpStatusCode.Conflict]: this.errorHandlers.handleEntitlementErrors,
          });

        return combineLatest([commandRequest.invoke$(), of(value)]);
      }),
      switchMap(([response, value]) => {
        if (response.error && response.errorActions) {
          return response.errorActions;
        }
        const { attributes, available, name } = value.entitlementData.entitlement;
        let messageBody = '';
        let customBtn;
        if (attributes.combineType === 'utilityToken') {
          messageBody = `${value.payload.quantity === available ? 'Full' : value.payload.quantity} ${name} allocated to ${
            value.organizationName
          }.`;
        } else {
          messageBody = `${name} allocated to ${value.organizationName}.`;
          customBtn = {
            label: 'Invite Users',
            navigateTo: '/invite-users',
          };
        }

        return [
          closeAllOpenDialogs(),
          refreshUserEntitlementsGrid(),
          getUnallocatedEntitlementsCount(),
          refreshOrgEntitlements({ value: String(value.tenantId) }),
          UiStateActions.displayMessage({
            payload: {
              message: messageBody,
              type: 'Success',
              customBtn: customBtn,
            },
          }),
          getTenantEffectiveRolesAndServices({ tenantId: String(value.tenantId) }),
          clearUserEntitlementsGridRefresh(),
          clearOrgEntitlementsRefresh(),
        ];
      }),
    );
  });

  applyCustomProvisioningEntitlement$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(applyCustomProvisioningEntitlement),
      switchMap((value) => {
        const url = `/api/tenant/entitlements/${value.entitlementData.entitlement.id}`;
        const productFamilyName = (value.entitlementData?.entitlement as AGGridEntitlement)?.productFamily;
        const commandRequest = this.commandRequestBuilderService
          .new(
            url,
            'POST',
            (msg) =>
              msg.type === TenantEvent.TrialAllocationUserActionPending ||
              msg.type === TenantEvent.ServiceAdditionStarted,
          )
          .withTenantHeader(value.tenantId)
          .withBody(value.payload)
          .withContext({ productFamilyName, requestInfo: value })
          .withWaitOn202Accepted()
          .withErrorHandler({
            [HttpStatusCode.BadRequest]: this.errorHandlers.handleEntitlementErrors,
            [HttpStatusCode.Conflict]: this.errorHandlers.handleEntitlementErrors,
          });
        return combineLatest([commandRequest.invoke$(), of(value)]);
      }),
      switchMap(([response, value]) => {
        if (response.error && response.errorActions) {
          return response.errorActions;
        }
        const pendinServiceEvent = new DomainEvent<PendingServiceV2>(response.message);
        const serviceId = pendinServiceEvent.EventData.service.serviceId;
        return [
          closeAllOpenDialogs(),
          initializeCustomProvisioning({
            payload: {
              app: value.entitlementData.app,
              tenantId: value.tenantId,
              entitlement: value.entitlementData.entitlement,
              serviceId: serviceId,
              userId: value.userId,
            },
          }),
        ];
      }),
    );
  });

  openIframe$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(initializeCustomProvisioning),
      switchMap((currentPSD) => {
        this.router.navigate(['/entitlement/hub'], {
          queryParams: {
            tenantId: currentPSD.payload.tenantId,
            serviceId: currentPSD.payload.serviceId,
          },
        });
        return [waitForCustomProvisioningComplete({ payload: currentPSD.payload })];
      }),
    );
  });

  waitForCustomProvisioningComplete$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(setDomainMessageReceived),
      filter((payload) => payload.payload.type === TenantEvent.ServiceAddedV2),
      mergeMap((payload) => combineLatest([of(payload), this.currentCustomProvisioningData$])),
      switchMap(([payload, currentPSD]) => {
        const actions: Action[] = [];
        const data = anyObjectTo<PendingService>(payload.payload.data);
        if (data.serviceId !== currentPSD?.serviceId) {
          actions.push(emptyAction());
          return actions;
        }
        actions.push(
          getTenantEffectiveRolesAndServices({
            tenantId: String(currentPSD.tenantId),
            navigateTo: this.ENTITLEMENT_PATH,
          }),
          refreshOrgEntitlements({ value: String(currentPSD.tenantId) }),
          customProvisioningCompleted(),
        );
        return actions;
      }),
    );
  });

  waitForCustomProvisioningCancelation$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(setDomainMessageReceived),
      filter((payload) => payload.payload.type === TenantEvent.ServiceAdditionCancelled),
      mergeMap((payload) => combineLatest([of(payload), this.currentCustomProvisioningData$])),

      //delay(1500),
      // NOTE - fixed the event type when client cancels CSP flow and we have to run compensating action
      // this delay was probably a workaround - https://github.com/Rockwell-Automation-Inc/LeMans-Home-UI/pull/1206 that
      // had the wrong event; now since we changed the event type, removing the delay.

      switchMap(([payload, currentPSD]) => {
        const actions: Action[] = [];
        // REVISIT: Remove - now that we have parsed json from the server
        const data: PendingServiceV2 = payload.payload.data;

        if (data.service.serviceId !== currentPSD?.serviceId) {
          actions.push(emptyAction());
          return actions;
        }

        actions.push(
          getTenantEffectiveRolesAndServices({ tenantId: String(currentPSD.tenantId) }),
          getTenantServices({
            payload: {
              tenantId: String(currentPSD.tenantId),
              userId: String(currentPSD.userId),
            },
          }),
          applyEntitlementCancelled(),
          leaveCustomProvisioning(),
        );

        this.router.navigate([this.ENTITLEMENT_PATH]);
        return actions;
      }),
    );
  });

  deallocateEntitlement$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(deallocateEntitlement),
      switchMap((payload) => {
        const entitlemenId = payload.entitlement.id;
        const url = `/api/tenant/entitlements/${entitlemenId}/deallocate`;
        const commandRequest = this.commandRequestBuilderService
          .new(
            url,
            'POST',
            (msg) =>
              msg.type === EntitlementEvent.EntitlementDeallocated2 ||
              msg.type === EntitlementEvent.EntitlementDeallocated ||
              msg.type === EntitlementEvent.EntitlementConsumptionReversedV2,
          )
          .withBody({ ...payload.requestBody })
          .withTenantHeader(payload.tenantId)
          .withWaitOn202Accepted()
          .withErrorHandler({
            [HttpStatusCode.BadRequest]: this.errorHandlers.handleEntitlementErrors,
          });

        return combineLatest([commandRequest.invoke$(), of(payload)]);
      }),
      concatLatestFrom(() => this.userID$),
      switchMap(([[response, payload], userId]) => {
        if (response.error && response.errorActions) {
          return response.errorActions;
        }
        return [
          closeAllOpenDialogs(),
          getTenantEffectiveRolesAndServices({ tenantId: String(payload.tenantId) }),
          refreshOrgEntitlements({ value: String(payload.tenantId) }),
          refreshUserEntitlementsGrid(),
          getUnallocatedEntitlementsCount(),
          getTenantServices({
            payload: {
              tenantId: String(payload.tenantId),
              userId: String(userId),
            },
          }),
          UiStateActions.displayMessage({
            payload: {
              message: `${payload.entitlement['detailServiceDataHeaders'].header} deallocated from ${payload.organizationName}.`,
              type: 'Success',
            },
          }),
          clearUserEntitlementsGridRefresh(),
          clearOrgEntitlementsRefresh(),
        ];
      }),
    );
  });

  updateAccess$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(updateAccess),
      mergeMap((updateAccessList) => {
        const dispatchAccessList: Action[] = [];
        const list = updateAccessList.payload;
        list.forEach((item) => {
          if (item.action === 'add') {
            dispatchAccessList.push(
              addAccess({ flow: updateAccessList.flow, tenantId: updateAccessList.tenantId, value: item }),
            );
          } else if (item.action === 'remove') {
            dispatchAccessList.push(
              removeAccess({
                payload: item,
              }),
            );
          }
        });
        return dispatchAccessList;
      }),
    );
  });

  addSingleAccess$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(addAccess),
      mergeMap((resourceAccess) => {
        const resourceAccessData = resourceAccess.value;
        const bodyRequest = {
          resourceId: resourceAccessData.resourceId,
          role: resourceAccessData.role,
        };
        const commandRequest = this.commandRequestBuilderService
          .new(ApiEndPoints.Users + resourceAccess.value.userId + '/roles', 'POST', UserEvent.UserRoleAssigned)
          .withTenantHeader(resourceAccess.tenantId)
          .withBody(bodyRequest)
          .withWaitOn202Accepted();
        return combineLatest([of(resourceAccess.flow), commandRequest.invoke$()]);
      }),
      mergeMap(([flow, response]) => {
        if (response.error && response.errorActions) {
          return response.errorActions;
        }

        let message = '';
        if (flow === 'add') {
          message = CommonConstants.responseMessageIconColorMap['add-access'].message;
        } else {
          message = CommonConstants.responseMessageIconColorMap['edit-access'].message;
        }
        if (flow !== 'approve') {
          this.router.navigate(['/access-mngmt']);
        }
        return [
          addAccessSuccess(),
          UiStateActions.displayMessage({
            payload: {
              message: message,
              type: 'Success',
            },
          }),
        ];
      }),
    );
  });

  removeAccess$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(removeAccess),
      mergeMap((removeData) => {
        const data = removeData.payload;
        const commandRequest = this.commandRequestBuilderService
          .new(
            ApiEndPoints.Users + data.userId + '/roles/?resid=' + data.resourceId,
            'DELETE',
            UserEvent.UserRoleRevoked,
          )
          .withTenantHeader(String(data?.tenantId))
          .withWaitOn200Ok();

        return commandRequest.invoke$();
      }),
      mergeMap((response) => {
        if (response.error && response.errorActions) {
          return response.errorActions;
        }

        this.router.navigate(['/access-mngmt']);
        return [
          refreshUserRolesGrid(),
          UiStateActions.displayMessage({
            payload: {
              message: 'Access has been removed successfully.',
              type: 'Info',
            },
          }),
          clearUserRolesGridRefresh(),
        ];
      }),
    );
  });

  mapActionsForUploadOrgLogo(actions: Action[], preOperation: UploadLogoPreOperation, tenantId: string): Action[] {
    const displayMessageAction = UiStateActions.displayMessage({
      payload: {
        message: 'Organization updated successfully.',
        type: 'Success',
      },
    });

    switch (preOperation) {
      case UploadLogoPreOperation.CreateTenant: {
        actions.push(switchTenant({ value: tenantId }));
        break;
      }
      case UploadLogoPreOperation.UpdateTenantName: {
        actions.push(displayMessageAction, getUserPreferences());
        break;
      }
      case UploadLogoPreOperation.UpdateTenant:
      case UploadLogoPreOperation.None:
      default: {
        actions.push(displayMessageAction, getTenantDetails({ value: tenantId }));
        break;
      }
    }
    return actions;
  }

  getTenantServices$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(getTenantServices),
      switchMap((payload) => {
        const userTenant = payload.payload;
        return this.dataService.getTenantServices$(userTenant.userId, userTenant.tenantId);
      }),
      switchMap((services) => {
        return [setTenantServices({ payload: services })];
      }),
    );
  });

  getUnAllocatedEntitlements$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(getUnallocatedEntitlementsCount),
      concatLatestFrom(() => this.userDataFacade.userID$),
      switchMap(([_payload, userId]) => {
        return this.dataService.getUnAllocatedEntitlements$(String(userId));
      }),
      switchMap((unAllocatedEntitlements) => {
        return [
          setUnallocatedEntitlementsCount({
            payload: unAllocatedEntitlements,
          }),
        ];
      }),
    );
  });

  getTenantUtilityTokens$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(getTenantUtilityTokens),
      switchMap((tenantId) => {
        return this.dataService.getTenantUtilityTokens$(tenantId.value);
      }),
      switchMap((tenantUtilityTokens: TenantUtilityTokens) => {
        // REVISIT: remove this piece of code when the backend provides the correct combineType
        tenantUtilityTokens.tokenEntitlements.forEach((entitlement) => {
          if (R.isEmpty(entitlement.attributes)) {
            entitlement.attributes = {
              combineType: EntitylementTypes.utilityToken,
            };
          }
        });
        tenantUtilityTokens.disabledTokenEntitlements.forEach((disabledEntitlement) => {
          if (R.isEmpty(disabledEntitlement.attributes)) {
            disabledEntitlement.attributes = {
              combineType: EntitylementTypes.utilityToken,
            };
          }
        });
        return [setTenantUtilityTokens({ payload: tenantUtilityTokens })];
      }),
    );
  });

  leaveTenant$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(leaveTenant),
      mergeMap((leaveTenantPayload) => {
        const commandRequest = this.commandRequestBuilderService
          .new(ApiEndPoints.Roles, 'DELETE', UserEvent.UserRoleRevoked)
          .withTenantHeader(String(leaveTenantPayload.payload.tenantId))
          .withWaitOn202Accepted()
          .withContext({ tenantType: leaveTenantPayload.payload.tenantType })
          .withErrorHandler({
            [HttpStatusCode.BadRequest]: this.errorHandlers.handleLeaveTenantErrors,
          });
        return combineLatest([of(leaveTenantPayload.payload), commandRequest.invoke$()]);
      }),
      mergeMap(([leaveTenantData, response]) => {
        const { tenantName, isFromSettingsPage, parentTenantId, isLeavingCurrentTenant } = leaveTenantData;
        if (response.error && response.errorActions) {
          return response.errorActions;
        }
        const actions: Action[] = [];
        actions.push(
          UiStateActions.displayMessage({
            payload: {
              message: `You are no longer a member of ${tenantName}`,
              type: 'Success',
            },
          }),
        );
        if (isFromSettingsPage) {
          if (parentTenantId) {
            actions.push(
              navigateBasedOnParentRole({
                switchToParentTenant: true,
                targetTenantId: parentTenantId,
              }),
            );
          } else {
            actions.push(noLastAccessedTenant(), updateHarnessOnNoTenantSelected());
          }
          return actions;
        }

        if (isLeavingCurrentTenant) {
          actions.push(noLastAccessedTenant(), updateHarnessOnNoTenantSelected());
        }

        actions.push(refreshTenantListGrid());
        return actions;
      }),
    );
  });

  transferOwnership$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(transferOwnership),
      switchMap((transferOwner) => {
        const { context, requestBody } = transferOwner.payload;
        const commandRequest = this.commandRequestBuilderService
          .new(`${ApiEndPoints.Tenant}owners`, 'PATCH', TenantEvent.ResourceOwnershipReassigned)
          .withTenantHeader(context.tenantId)
          .withBody({ ...requestBody })
          .withWaitOn202Accepted()
          .withTimeout(120)
          .withErrorHandler({
            [HttpStatusCode.BadRequest]: this.errorHandlers.handleTransferOwnershipErrors,
            [HttpStatusCode.Conflict]: this.errorHandlers.handleTransferOwnershipErrors,
            [HttpStatusCode.Forbidden]: this.errorHandlers.handleTransferOwnershipErrors,
          });

        return combineLatest([commandRequest.invoke$(), of(context)]);
      }),
      switchMap(([response, context]) => {
        const action: Action[] = [
          closeAllOpenDialogs(),
          getTenantDetails({ value: context.tenantId }),
          getTenantEffectiveRolesAndServices({ tenantId: context.tenantId }),
        ];

        if (response.error && response.errorActions) {
          return [...action, ...response.errorActions];
        }
        let message = `${context.ownerName} is now owner of ${context.tenantName} and any specified applications. You are now an admin.`;
        if (context.transferType === TransferOwnershipType.APPLICATION) {
          message = `${context.ownerName} is now owner of ${context.resourceName} in ${context.tenantName}. You are now an admin.`;
        }

        return [
          ...action,
          UiStateActions.displayMessage({
            payload: {
              message,
              type: 'Success',
            },
          }),
        ];
      }),
    );
  });

  changeParentOrganization$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(changeParentOrganization),
      switchMap((actionProps) => {
        const payload = actionProps.payload;
        const requestBody = {
          child: payload.subOrgId,
          parent: payload.currentParentOrgId,
          destination: payload.newParentOrgId,
        };
        const commandRequest = this.commandRequestBuilderService
          .new(`${ApiEndPoints.Tenant}hierarchy/${actionProps.idempotencyKey}/transfer`, 'PUT', TenantEvent.Synced)
          .withTenantHeader(requestBody.child)
          .withBody({ ...requestBody })
          .withWaitOn202Accepted()
          .withErrorHandler({
            [HttpStatusCode.BadRequest]: this.errorHandlers.handleTenantHierarchyErrors,
            [HttpStatusCode.Conflict]: this.errorHandlers.handleTenantHierarchyErrors,
            [HttpStatusCode.Forbidden]: this.errorHandlers.handleTenantHierarchyErrors,
          });

        return combineLatest([commandRequest.invoke$(), of(payload)]);
      }),
      switchMap(([response, payload]) => {
        const action: Action[] = [];
        if (response.error && response.errorActions) {
          return [...action, ...response.errorActions];
        }
        const message = `${payload.subOrgName} has been transferred to ${payload.newParentOrgName}.`;
        action.push(
          changeParentOrganizationSuccess(),
          getTenantDetails({ value: payload.subOrgId }),
          getTenantEffectiveRolesAndServices({ tenantId: payload.subOrgId }),
        );
        return [
          ...action,
          UiStateActions.displayMessage({
            payload: {
              message,
              type: 'Success',
            },
          }),
        ];
      }),
    );
  });

  checkUserCanChangeTenantHierarchy$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(checkUserCanChangeTenantHierarchy),
      switchMap((payload) => {
        return this.dataService.getTenantEffectiveRoles$(payload.tenantId, payload.userId);
      }),
      switchMap((effectiveRoles: EffectiveRole[]) => {
        const canChangeTenantHierarchy = effectiveRoles.some(
          (role) => role.resourceType === 'Tenant' && ROLES_WITH_MANAGE_ACCESS.includes(role.role),
        );
        return [setCanChangeTenantHierarchy({ value: canChangeTenantHierarchy })];
      }),
    );
  });

  convertToSubOrganization$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(convertToSubOrganization),
      switchMap((actionProps) => {
        const { context, requestBody } = actionProps.payload;

        const commandRequest = this.commandRequestBuilderService
          .new(`${ApiEndPoints.Tenant}hierarchy/${context.idempotencyKey}/link`, 'PUT', TenantEvent.Synced)
          .withTenantHeader(context.tenantId)
          .withBody({ ...requestBody })
          .withWaitOn202Accepted()
          .withErrorHandler({
            [HttpStatusCode.BadRequest]: this.errorHandlers.handleTenantHierarchyErrors,
            [HttpStatusCode.Conflict]: this.errorHandlers.handleTenantHierarchyErrors,
            [HttpStatusCode.Forbidden]: this.errorHandlers.handleTenantHierarchyErrors,
          });
        return combineLatest([commandRequest.invoke$(), of(context)]);
      }),
      switchMap(([response, context]) => {
        if (response.error && response.errorActions) {
          return response.errorActions;
        }
        const action: Action[] = [
          convertToSubOrganizationSuccess(),
          getTenantDetails({ value: context.tenantId }),
          getTenantEffectiveRolesAndServices({ tenantId: context.tenantId }),
        ];

        const message = `${context.tenantName} has been converted to a sub-organization of ${context.parentName}.`;
        return [
          ...action,
          UiStateActions.displayMessage({
            payload: {
              message,
              type: 'Success',
            },
          }),
        ];
      }),
    );
  });

  convertToParentOrganization$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(convertToParentOrganization),
      switchMap((convertToParentOrg) => {
        const { context, requestBody } = convertToParentOrg.payload;
        const commandRequest: CommandRequest = this.commandRequestBuilderService
          .new(`${ApiEndPoints.Tenant}hierarchy/${context.idempotencyKey}/unlink`, 'PUT', TenantEvent.Synced)
          .withTenantHeader(context.tenantId)
          .withBody(requestBody)
          .withWaitOn202Accepted()
          .withErrorHandler({
            [HttpStatusCode.BadRequest]: this.errorHandlers.handleTenantHierarchyErrors,
            [HttpStatusCode.Conflict]: this.errorHandlers.handleTenantHierarchyErrors,
            [HttpStatusCode.Forbidden]: this.errorHandlers.handleTenantHierarchyErrors,
          });

        return combineLatest([commandRequest.invoke$(), of(convertToParentOrg)]);
      }),
      switchMap(([response, convertToParentOrg]) => {
        const {
          context: { tenantId, tenantName },
        } = convertToParentOrg.payload;
        if (response.error && response.errorActions) {
          return response.errorActions;
        } else {
          return [
            closeAllOpenDialogs(),
            convertToParentOrganizationSuccess(),
            UiStateActions.displayMessage({
              payload: {
                message: `${tenantName} was promoted and is now a top-level organization`,
                type: 'Success',
              },
            }),
            navigateBasedOnParentRole({
              targetTenantId: tenantId,
              refreshTenantDetails: true,
            }),
          ];
        }
      }),
    );
  });

  navigateBasedOnParentRole$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(navigateBasedOnParentRole),
      concatLatestFrom(() => this.userID$),
      switchMap(([payload, userId]) => {
        return combineLatest([
          this.dataService.getTenantEffectiveRoles$(payload.targetTenantId, String(userId)),
          of(payload),
        ]);
      }),
      switchMap(([effectiveRoles, payload]) => {
        const { targetTenantId, refreshTenantDetails, switchToParentTenant } = payload;
        const actions: Action[] = [];
        if (effectiveRoles.length) {
          if (refreshTenantDetails) {
            actions.push(getTenantDetails({ value: targetTenantId }));
          } else if (switchToParentTenant) {
            actions.push(
              setTenantId({
                value: targetTenantId,
                navigateTo: `${TENANT_MGMT_ROUTES.ORGANIZATIONS}/${targetTenantId}/suborgs`,
              }),
            );
          }
        } else {
          actions.push(userLosesAccessOnParentTenant(), navigateTo({ path: TENANT_MGMT_ROUTES.ORGANIZATIONS }));
        }
        return actions;
      }),
    );
  });

  getUserMembership$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(getUserMembership),
      switchMap((payload) => {
        return this.dataService.getUserMembership$(payload.tenantId, payload.userId);
      }),
      switchMap((roles: UserMembership) => {
        return [setUserMembership({ payload: roles })];
      }),
    );
  });

  updateAccessibleTenants$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(
        acceptInvitationSuccess,
        archiveTenantSuccess,
        unarchiveTenantSuccess,
        updateTenantNameSuccess,
        createTenantSuccess,
        changeParentOrganizationSuccess,
        convertToSubOrganizationSuccess,
        convertToParentOrganizationSuccess,
      ),
      concatLatestFrom(() => this.userDataFacade.userID$),
      switchMap(([action, userId]) => {
        return combineLatest([this.navbarCommonService.getUserAccessibleTenants$(userId, ''), of(action)]);
      }),
      switchMap(([accessibleTenants, action]) => {
        const actions: Action[] = [
          updateAccessibleTenants({ accessibleTenants: accessibleTenants as AccessibleTenant[] }),
        ];
        // After updating accessible tenants, switch to the new tenant for invitation flow
        if (action?.type === acceptInvitationSuccess.type && action.tenantId) {
          actions.push(switchTenant({ value: action.tenantId, navigateTo: '/dashboard' }));
        }
        return actions;
      }),
    );
  });

  getTenantEffectiveRoles$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(getTenantEffectiveRoles),
      switchMap((payload) => {
        return this.dataService.getTenantEffectiveRoles$(payload.tenantId, payload.userId);
      }),
      switchMap((effectiveRoles: EffectiveRole[]) => {
        return [setTenantEffectiveRoles({ effectiveRoles: effectiveRoles })];
      }),
    );
  });

  getLoggedInUserRoleHierarchy$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(getLoggedInUserRoleHierarchy),
      concatLatestFrom(() => this.userID$),
      switchMap(([payload, userId]) => this.dataService.getUserRoleHierarchy$(payload.tenantId, userId)),
      map((userRoleHierarchy) => setLoggedInUserRoleHierarchy({ userRoleHierarchy: userRoleHierarchy })),
    );
  });

  getTenantResourceHierarchy$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(getTenantResourceHierarchy),
      switchMap((payload) => this.dataService.getTenantResourceHierarchy$(payload.tenantId)),
      map((resourceHierarchy) => setTenantResourceHierarchy({ resourceHierarchy: resourceHierarchy })),
    );
  });

  updateUserDescriptionForAllocationId$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(updateUserDescriptionForAllocationId),
      concatMap((payload) => {
        const { allocationId, entitlementId, userDescription, tenantId } = payload;
        const url = `/api/tenant/entitlements/${entitlementId}/allocations/${allocationId}/userDescription`;

        const commandRequest = this.commandRequestBuilderService
          .new(url, 'PATCH')
          .withBody({ value: userDescription })
          .withTenantHeader(tenantId);
        return combineLatest([commandRequest.invoke$(), of(payload)]);
      }),
      switchMap(([response, payload]) => {
        const { allocationId, entitlementId, userDescription } = payload;
        if (response.error) {
          return [
            updateUserDescriptionOnFailure({ allocationId, entitlementId }),
            UiStateActions.displayMessage({
              payload: {
                message: 'User Description update failed. Please try again.',
                type: 'Error',
              },
            }),
          ];
        } else {
          return [updateUserDescriptionOnSuccess({ allocationId, entitlementId, userDescription })];
        }
      }),
    );
  });
}
